import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type {
  QualityImprovementPanelProps,
  QualitySuggestion,
  PrioritizedRecommendation
} from '../../types/advanced_content_quality_ui';

// Severity styling configuration
const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-red-50 border-red-200',
    text: 'text-red-800',
    badge: 'bg-red-100 text-red-800',
    icon: '🚨'
  },
  high: {
    bg: 'bg-orange-50 border-orange-200',
    text: 'text-orange-800',
    badge: 'bg-orange-100 text-orange-800',
    icon: '⚠️'
  },
  medium: {
    bg: 'bg-yellow-50 border-yellow-200',
    text: 'text-yellow-800',
    badge: 'bg-yellow-100 text-yellow-800',
    icon: '⚡'
  },
  low: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-800',
    icon: '💡'
  }
};

// Effort styling configuration
const EFFORT_STYLES = {
  low: { color: 'text-green-600', bg: 'bg-green-100' },
  medium: { color: 'text-yellow-600', bg: 'bg-yellow-100' },
  high: { color: 'text-red-600', bg: 'bg-red-100' }
};

export const QualityImprovementPanel: React.FC<QualityImprovementPanelProps> = ({
  recommendations,
  allowAutoApply = false,
  showPrioritization = true,
  onRecommendationSelect,
  onRecommendationApply,
  onRecommendationReject
}) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [applyingRecommendations, setApplyingRecommendations] = useState<Set<string>>(new Set());
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());

  // Filter and sort suggestions
  const filteredSuggestions = useMemo(() => {
    let filtered = recommendations.automatedSuggestions;

    if (filterSeverity !== 'all') {
      filtered = filtered.filter(s => s.severity === filterSeverity);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(s => s.type === filterType);
    }

    // Sort by severity and expected improvement
    return filtered.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
      if (severityDiff !== 0) return severityDiff;
      return b.expectedImprovement - a.expectedImprovement;
    });
  }, [recommendations.automatedSuggestions, filterSeverity, filterType]);

  // Get prioritized recommendations if available
  const prioritizedRecommendations = useMemo(() => {
    if (!showPrioritization) return [];
    return recommendations.prioritizedRecommendations
      .filter(pr => filteredSuggestions.some(s => s.id === pr.suggestion.id))
      .sort((a, b) => b.priority - a.priority);
  }, [recommendations.prioritizedRecommendations, filteredSuggestions, showPrioritization]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: QualitySuggestion) => {
    const newSelection = selectedSuggestion === suggestion.id ? null : suggestion.id;
    setSelectedSuggestion(newSelection);
    onRecommendationSelect?.(suggestion);
  };

  // Handle suggestion application
  const handleApplyRecommendation = async (suggestion: QualitySuggestion) => {
    if (!onRecommendationApply) return;

    setApplyingRecommendations(prev => new Set(prev).add(suggestion.id));
    
    try {
      await onRecommendationApply(suggestion);
    } catch (error) {
      console.error('Error applying recommendation:', error);
    } finally {
      setApplyingRecommendations(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestion.id);
        return newSet;
      });
    }
  };

  // Handle suggestion rejection
  const handleRejectRecommendation = (suggestion: QualitySuggestion, reason: string) => {
    onRecommendationReject?.(suggestion, reason);
  };

  // Toggle suggestion expansion
  const toggleSuggestionExpansion = (suggestionId: string) => {
    setExpandedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(suggestionId)) {
        newSet.delete(suggestionId);
      } else {
        newSet.add(suggestionId);
      }
      return newSet;
    });
  };

  // Render suggestion card
  const renderSuggestionCard = (suggestion: QualitySuggestion, priorityInfo?: PrioritizedRecommendation) => {
    const isSelected = selectedSuggestion === suggestion.id;
    const isExpanded = expandedSuggestions.has(suggestion.id);
    const isApplying = applyingRecommendations.has(suggestion.id);
    const severityStyle = SEVERITY_STYLES[suggestion.severity];
    const effortStyle = EFFORT_STYLES[suggestion.effort];

    return (
      <Card
        key={suggestion.id}
        className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        } ${severityStyle.bg}`}
        onClick={() => handleSuggestionSelect(suggestion)}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-lg">{severityStyle.icon}</span>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${severityStyle.badge}`}>
                  {suggestion.severity.toUpperCase()}
                </span>
                <span className="text-sm text-gray-600 capitalize">{suggestion.type}</span>
                {priorityInfo && showPrioritization && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Priority #{priorityInfo.priority}
                  </span>
                )}
              </div>
            </div>
            
            <h3 className={`font-semibold mb-2 ${severityStyle.text}`}>
              {suggestion.title}
            </h3>
            
            <p className="text-gray-700 text-sm mb-3">
              {suggestion.description}
            </p>

            {/* Metrics Row */}
            <div className="flex items-center space-x-4 mb-3">
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500">Expected improvement:</span>
                <span className="text-sm font-medium text-green-600">+{suggestion.expectedImprovement}%</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500">Effort:</span>
                <span className={`text-xs font-medium px-2 py-1 rounded ${effortStyle.bg} ${effortStyle.color}`}>
                  {suggestion.effort}
                </span>
              </div>
              {suggestion.automationAvailable && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  🤖 Auto-apply available
                </span>
              )}
            </div>

            {/* ROI Information for prioritized recommendations */}
            {priorityInfo && showPrioritization && (
              <div className="grid grid-cols-3 gap-4 mb-3 p-3 bg-white rounded border">
                <div className="text-center">
                  <div className="text-xs text-gray-500">ROI Score</div>
                  <div className="text-sm font-semibold text-purple-600">{priorityInfo.roi.toFixed(1)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Impact Score</div>
                  <div className="text-sm font-semibold text-blue-600">{priorityInfo.impactScore}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Est. Time</div>
                  <div className="text-sm font-semibold text-gray-600">{priorityInfo.estimatedTime}m</div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 ml-4">
            {allowAutoApply && suggestion.automationAvailable && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApplyRecommendation(suggestion);
                }}
                size="small"
                variant="success"
                disabled={isApplying}
              >
                {isApplying ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-1">Applying...</span>
                  </>
                ) : (
                  'Auto Apply'
                )}
              </Button>
            )}
            
            <Button
              onClick={(e) => {
                e.stopPropagation();
                toggleSuggestionExpansion(suggestion.id);
              }}
              size="small"
              variant="ghost"
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
            {/* Specific Location */}
            {suggestion.specificLocation && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Location</h4>
                <div className="text-sm text-gray-600 bg-white p-3 rounded border">
                  <div>Section: {suggestion.specificLocation.section}</div>
                  {suggestion.specificLocation.paragraph && (
                    <div>Paragraph: {suggestion.specificLocation.paragraph}</div>
                  )}
                  {suggestion.specificLocation.sentence && (
                    <div>Sentence: {suggestion.specificLocation.sentence}</div>
                  )}
                </div>
              </div>
            )}

            {/* Suggested Change */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Suggested Change</h4>
              <div className="text-sm text-gray-700 bg-white p-3 rounded border">
                {suggestion.suggestedChange}
              </div>
            </div>

            {/* Dependencies */}
            {priorityInfo?.dependencies && priorityInfo.dependencies.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Dependencies</h4>
                <div className="space-y-1">
                  {priorityInfo.dependencies.map((dep, index) => (
                    <div key={index} className="text-sm text-gray-600 bg-white p-2 rounded border text-sm">
                      {dep}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-3 pt-2">
              {!suggestion.automationAvailable && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApplyRecommendation(suggestion);
                  }}
                  size="small"
                  disabled={isApplying}
                >
                  {isApplying ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-1">Applying...</span>
                    </>
                  ) : (
                    'Apply Manually'
                  )}
                </Button>
              )}
              
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  const reason = prompt('Please provide a reason for rejecting this recommendation:');
                  if (reason) {
                    handleRejectRecommendation(suggestion, reason);
                  }
                }}
                size="small"
                variant="ghost"
              >
                Reject
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Get unique types for filtering
  const availableTypes = useMemo(() => {
    const types = new Set(recommendations.automatedSuggestions.map(s => s.type));
    return Array.from(types);
  }, [recommendations.automatedSuggestions]);

  return (
    <div className="quality-improvement-panel space-y-6">
      {/* Header and Filters */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quality Improvements</h2>
          <p className="text-gray-600 mt-1">
            {filteredSuggestions.length} recommendation{filteredSuggestions.length !== 1 ? 's' : ''} available
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Severity Filter */}
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
          >
            <option value="all">All Types</option>
            {availableTypes.map(type => (
              <option key={type} value={type} className="capitalize">
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(['critical', 'high', 'medium', 'low'] as const).map(severity => {
          const count = recommendations.automatedSuggestions.filter(s => s.severity === severity).length;
          const style = SEVERITY_STYLES[severity];
          
          return (
            <Card key={severity} className={`p-4 ${style.bg}`}>
              <div className="flex items-center space-x-3">
                <span className="text-xl">{style.icon}</span>
                <div>
                  <div className="text-sm text-gray-600 capitalize">{severity} Priority</div>
                  <div className={`text-xl font-semibold ${style.text}`}>{count}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Implementation Tracking Summary */}
      {recommendations.implementationTracking.appliedSuggestions.length > 0 && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center space-x-3">
            <span className="text-green-500 text-xl">✅</span>
            <div>
              <h3 className="text-green-800 font-semibold">Recent Improvements Applied</h3>
              <p className="text-green-700 text-sm mt-1">
                {recommendations.implementationTracking.appliedSuggestions.length} recommendations 
                applied recently with average improvement of{' '}
                {(recommendations.implementationTracking.appliedSuggestions
                  .reduce((sum, applied) => sum + applied.actualImprovement, 0) / 
                  recommendations.implementationTracking.appliedSuggestions.length).toFixed(1)}%
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Recommendations List */}
      <div className="space-y-4">
        {filteredSuggestions.length === 0 ? (
          <Card className="p-8 text-center">
            <span className="text-4xl mb-4 block">🎉</span>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Recommendations</h3>
            <p className="text-gray-600">
              Great job! No quality improvements needed with the current filters.
            </p>
          </Card>
        ) : (
          <>
            {showPrioritization && prioritizedRecommendations.length > 0 ? (
              prioritizedRecommendations.map(pr => 
                renderSuggestionCard(pr.suggestion, pr)
              )
            ) : (
              filteredSuggestions.map(suggestion => 
                renderSuggestionCard(suggestion)
              )
            )}
          </>
        )}
      </div>

      {/* Effectiveness Validation Summary */}
      {recommendations.effectivenessValidation.successRate > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-blue-800 font-semibold">Recommendation Effectiveness</h3>
              <p className="text-blue-700 text-sm mt-1">
                Success rate: {(recommendations.effectivenessValidation.successRate * 100).toFixed(1)}% based on{' '}
                {recommendations.effectivenessValidation.beforeAfterComparisons.length} applied recommendations
              </p>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {(recommendations.effectivenessValidation.successRate * 100).toFixed(0)}%
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default QualityImprovementPanel;
